import React from 'react';
import { Box, Card, CardContent, Typography, TextField, Button } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { UserService } from '../../api/services/userservice';
import { showAlertDialog } from '../../store/Reducers/AlertDialogSlice';
import { logout } from '../../store/Reducers/UserSlice';
import { IChangePasswordFormValues } from '../../types/changePassword';

export const ChangePasswordPage: React.FC = () => {
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<IChangePasswordFormValues>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (data: IChangePasswordFormValues) => {
    try {
      await UserService.changePassword({
        current_password: data.currentPassword,
        new_password: data.newPassword,
      });

      dispatch(
        showAlertDialog({
          title: 'You have successfully updated your password. Please relogin.',
          description: '',
          confirmText: 'Logout',
          onConfirm: () => {
            dispatch(logout());
            localStorage.clear();
          },
        }),
      );
    } catch (err: any) {
      dispatch(
        showNotification({
          message: `Failed to change password. ${err?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '80vh',
      }}
    >
      <Card sx={{ minWidth: 400, p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Password Expired
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You must change your password to continue using the dashboard.
          </Typography>

          {/* Current Password */}
          <Controller
            name="currentPassword"
            control={control}
            rules={{ required: 'Current password is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Current Password"
                type="password"
                fullWidth
                margin="normal"
                error={!!errors.currentPassword}
                helperText={errors.currentPassword?.message}
              />
            )}
          />

          {/* New Password */}
          <Controller
            name="newPassword"
            control={control}
            rules={{
              required: 'New password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="New Password"
                type="password"
                fullWidth
                margin="normal"
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
              />
            )}
          />

          {/* Confirm Password */}
          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: 'Confirm password is required',
              validate: (value) => value === newPassword || 'Passwords do not match',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Confirm Password"
                type="password"
                fullWidth
                margin="normal"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
              />
            )}
          />

          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleSubmit(onSubmit)}
          >
            Update Password
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};
